import { Component, EventEmitter, Inject, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { Course } from '@app/app/page-modules/course/models/course.model';
import { PlaylistReportComponent } from '@app/app/page-modules/playlist/components/view/content/report/report.component';
import { Program } from '@app/app/page-modules/programs/models/program.model';
import { SocialShareUrlV3Component } from '@app/app/shared/components/social-share-v3/social-share-url-v3.component';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { SnackbarHelper } from '@app/app/shared/helpers/snackbar-helper';
import { AvailableLanguage, Organization, Resource } from '@app/app/shared/models';
import { LearnerPlaylistReport } from '@app/app/shared/models/playlist/playlist-report.model';
import { LEARNER_PLAYLIST_DATA_SERVICE, LearnerPlaylistDataService } from '@app/app/shared/services/learner-playlist/data.service';
import { LoaderService } from '@app/app/shared/services/loader/loader.service';
import { UserDetailsSummary } from '@app/app/user-auth/models';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import {
  ArrowDown01Icon,
  Bookmark02Icon,
  BookmarkCheck02Icon,
  Download04Icon,
  LanguageSquareIcon,
  Share08Icon,
} from '@hugeicons/core-free-icons/index';
import { LucideBookmark, LucideBookmarkCheck, LucideChevronDown, LucideDownload, LucideLanguages, LucideShare2 } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'ptl-content-item-control-panel',
  templateUrl: './content-item-control-panel.component.html',
  styleUrls: ['./content-item-control-panel.component.scss'],
})
export class ContentItemControlPanelComponent implements OnInit, OnDestroy {
  @Input() program: Program | Course | Resource;
  @Input() programUid: string;
  @Input() programUri: string;
  @Input() favorite: boolean;
  @Input() hideTitle = false;

  @Output() favoriteToggle = new EventEmitter<void>();
  @Output() languageChanged = new EventEmitter<string>();

  @Select(UserAuthState.userDetailsData)
  userDetailsData$: Observable<UserDetailsSummary>;

  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;
  availableLanguages: AvailableLanguage[];
  selectedLanguageCode: string;
  userUid: string;
  isProject = false;
  isIframeMode: boolean;
  report: LearnerPlaylistReport;
  downloadInProgress: boolean;

  private subscriptionEnd$ = new EventEmitter<void>();
  @ViewChild('playlistReportComponent') playlistReportComponent: PlaylistReportComponent;

  constructor(
    private dialog: MatDialog,
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    private loaderService: LoaderService,
    private translocoService: TranslocoService,
    private router: Router,
    @Inject(LEARNER_PLAYLIST_DATA_SERVICE) private playlistDataService: LearnerPlaylistDataService,
    @Inject(LEARNER_PLAYLIST_DATA_SERVICE) private learnerPlaylistDataService: LearnerPlaylistDataService,
  ) {
    this.selectedLanguageCode = LanguageCodeHelper.getOnlyContentLanguageCode();
  }

  ngOnInit() {
    this.availableLanguages = (this.program as Program | Course).availableLanguages ?? (this.program as Resource).availableLanguages ?? [];
  }

  get displayTitle(): string {
    const prog = this.program as Program | Course;
    const res = this.program as Resource;
    return prog?.title ?? res?.content?.headline ?? '';
  }

  // TODO: Revert for next release - remove isCard check, show save button for all content types
  get isCard(): boolean {
    return (this.program as Resource)?.content != null;
  }

  ngOnDestroy() {
    this.subscriptionEnd$?.emit();
  }

  shareProgram() {
    this.organizationData$.pipe(take(1)).subscribe((organization) => {
      const res = this.program as Resource;
      const shareUrl =
        res?.content != null
          ? `${organization.site}/cards/${res.settings?.publication?.uri ?? res._id}`
          : `${organization.site}/programs/${(this.program as Program).uri}`;

      this.dialog.open(SocialShareUrlV3Component, {
        width: '40rem',
        maxWidth: '95vw',
        maxHeight: '80vh',
        direction: LanguageCodeHelper.getBodyLanguageDir(),
        panelClass: 'ptl-v3-dialog',
        backdropClass: 'ptl-v3-dialog-backdrop-default',
        autoFocus: false,
        data: {
          shareUrl: shareUrl,
        },
      });
    });
  }
  onReportDownloaded() {
    this.downloadInProgress = false;
    this.loaderService.hide();
  }

  onReportPdfError() {
    this.loaderService.hide();
    this.downloadInProgress = false;
    SnackbarHelper.showTranslatableSnackBar(
      this.ngZone,
      this.snackBar,
      this.translocoService,
      'translations.piReportGenerationError',
      5000,
    );
  }

  downloadReport() {
    // For cards, use browser print instead of playlist report
    if (this.isCard) {
      window.print();
      return;
    }

    if (!this.downloadInProgress) {
      this.downloadInProgress = true;

      if (!this.report) {
        this.playlistDataService.getReport(this.program._id).subscribe(({ isSuccess, value, error }) => {
          if (isSuccess) {
            this.report = {
              userName: 'hello',
              reportItems: value,
            };
          } else {
            SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error, 5000);
            this.downloadInProgress = false;
          }
        });
      } else {
        this.playlistReportComponent?.downloadPDF();
      }
    }
  }

  saveItem() {
    const p = this.program as { _id?: string; favorite?: boolean };
    if (p?.favorite) {
      this.learnerPlaylistDataService.removePlaylistFromFavorites(p?._id).subscribe(({ isSuccess }) => {
        if (isSuccess) {
          (this.program as { favorite?: boolean }).favorite = !p.favorite;
        }
      });
    } else {
      this.learnerPlaylistDataService.addPlaylistToFavorites(p?._id).subscribe(({ isSuccess }) => {
        if (isSuccess) {
          (this.program as { favorite?: boolean }).favorite = !p?.favorite;
        }
      });
    }
  }

  languageSelect(languageCode: string) {
    this.languageChanged.emit(languageCode);
    this.selectedLanguageCode = languageCode;
    LanguageCodeHelper.setContentLanguageCode(languageCode);
  }

  protected readonly BookmarkCheck02Icon = BookmarkCheck02Icon;
  protected readonly Bookmark02Icon = Bookmark02Icon;
  protected readonly Download04Icon = Download04Icon;
  protected readonly Share08Icon = Share08Icon;
  protected readonly LanguageSquareIcon = LanguageSquareIcon;
  protected readonly ArrowDown01Icon = ArrowDown01Icon;
  protected readonly LucideLanguages = LucideLanguages;
  protected readonly LucideChevronDown = LucideChevronDown;
  protected readonly LucideShare2 = LucideShare2;
  protected readonly LucideBookmark = LucideBookmark;
  protected readonly LucideBookmarkCheck = LucideBookmarkCheck;
  protected readonly LucideDownload = LucideDownload;
}
